import { useMemo } from 'react'

import { Box, IconButton, Text, VStack } from '@chakra-ui/react'
import { CollapseIcon, ExpandIcon, HideIcon, ShowIcon } from '@editor/assets/icons'
import { INFO_PANEL_PADDING } from '@editor/config/styles'
import { EditorPanelItem } from '@editor/interfaces/editor'
import { InspectionArea } from '@editor/interfaces/inspection'
import { InspectionGroup as IInspectionGroup } from '@editor/interfaces/inspectionGroup'
import { Tools } from '@editor/pages/projects/blueprint-editor/tools/setup'
import useUnlabeledColor from '@editor/pages/projects/editor/hooks/useUnlabeledColor'
import InspectionGroup from '@editor/pages/projects/editor/infoPanels/InspectionGroup'
import { Layer } from '@editor/pages/projects/editor/infoPanels/components/Layer'
import { findItemsByGroupId } from '@editor/pages/projects/editor/infoPanels/utils'
import { useAppSelector } from '@editor/store/app'

import useEditorElementEventHandlers from '../hooks/useEditorElementEventHandlers'

const EditorElements = () => {
  // Store
  const connectedAreas = useAppSelector((state) => state.page.connectedAreas)
  const inspectionGroups = useAppSelector((state) => state.inspectionGroup.groups)
  const isAllowedToModify = useAppSelector((state) => state.blueprint.editor.isAllowedToModify)
  const getUnlabeledColor = useUnlabeledColor()

  // Initialize all tools
  const toolsObjectsPanel = Tools.map((tool) =>
    tool.hooks?.useElementsPanel ? tool.hooks.useElementsPanel() : undefined,
  ).filter((config) => !!config)

  /**
   * Generate panel items for each tool
   */
  const toolPanelItems = useMemo((): Record<string, EditorPanelItem[]> => {
    const getItem = (item: EditorPanelItem, area?: InspectionArea): EditorPanelItem => ({
      ...item,
      children: (item.children?.map((child) => getItem(child, area)) || []).concat(
        item.item ? getPanelItems(item, area) : [],
      ),
      isCheckable: item.isCheckable ? isAllowedToModify : undefined,
    })

    const getPanelItems = (parent?: EditorPanelItem, area?: InspectionArea) => {
      const parents = toolsObjectsPanel
        .map((hook) => {
          if (!hook) return undefined

          let items: EditorPanelItem[] | undefined
          if (hook?.getPanelItems) {
            items = hook.getPanelItems(parent?.item, area).map((item) => getItem(item, area))
          }

          return items
        })
        .flat()
        .filter(Boolean) as EditorPanelItem[]

      return parents
    }

    return (
      connectedAreas?.reduce(
        (acc, { current }) => {
          acc[current.area.inspection_area_id] = [
            {
              key: 'unlabeled-elements',
              name: '未分類要素',
              children: getPanelItems(undefined, current.area),
              isVirtualContainer: true,
              color: getUnlabeledColor(current.area.inspection_area_id),
              textStyle: { fontWeight: 'bold', fontSize: '1.2em' },
            },
          ]
          return acc
        },
        {} as Record<string, EditorPanelItem[]>,
      ) || {}
    )
  }, [connectedAreas, toolsObjectsPanel, isAllowedToModify, getUnlabeledColor])

  /**
   * Memoized list of inspection groups  separated by area to be displayed.
   * This includes any callback functions since Redux does not allow functions in the store.
   */
  const groupLayers = useMemo(
    (): Record<string, EditorPanelItem[]> =>
      connectedAreas?.reduce(
        (acc, { current }) => {
          const groups = [...inspectionGroups]
            .filter((group) => (group.item as IInspectionGroup).inspection_area_id === current.area.inspection_area_id)
            .sort((a, b) => (a?.order || 0) - (b?.order || 0))
            .map((group) => ({
              ...group,
              children: group.item
                ? findItemsByGroupId(
                    (group.item as IInspectionGroup).group_id,
                    toolPanelItems[current.area.inspection_area_id],
                  ).sort((a, b) => (a.order || 0) - (b.order || 0))
                : undefined,
              isOrderable: false,
              isCheckable: isAllowedToModify,
              aggregateSelection: true,
              onDelete: false,
              onSelect: false,
            }))

          acc[current.area.inspection_area_id] = groups
          return acc
        },
        {} as Record<string, EditorPanelItem[]>,
      ) || {},
    [connectedAreas, inspectionGroups, toolPanelItems, isAllowedToModify],
  )

  const {
    collapsedAreas,
    isInvisible,
    handleSelectLayer,
    handleToggleVisibility,
    handleToggleAreaVisibility,
    handleCheckToggle,
    handleCollapseArea,
  } = useEditorElementEventHandlers(groupLayers, toolPanelItems)

  return (
    <Box className="editor-elements-panel">
      {connectedAreas?.map(({ current }) => (
        <Box key={`panel-editor-elements-area-${current.area.inspection_area_id}`}>
          <Box className="section-title">
            <IconButton
              minW={{
                base: 7,
                lg: 6,
              }}
              aria-label={collapsedAreas.includes(current.area.inspection_area_id) ? 'expand' : 'collapse'}
              onClick={(e) => {
                e.stopPropagation()
                handleCollapseArea(current.area.inspection_area_id)
              }}
              variant="panel-icon"
            >
              {collapsedAreas.includes(current.area.inspection_area_id) ? <ExpandIcon /> : <CollapseIcon />}
            </IconButton>
            <IconButton
              minW={{
                base: 7,
                lg: 6,
              }}
              aria-label={isInvisible(current.area.inspection_area_id) ? 'すべて表示' : 'すべて非表示'}
              variant="panel-icon"
              onClick={() => handleToggleAreaVisibility(current.area.inspection_area_id)}
            >
              {isInvisible(current.area.inspection_area_id) ? <HideIcon /> : <ShowIcon />}
            </IconButton>
            <Text>{current.area.inspection_area_name}</Text>
          </Box>
          <Box display={collapsedAreas.includes(current.area.inspection_area_id) ? 'none' : 'block'}>
            {/* Inspection groups list */}
            <InspectionGroup
              hideTitle
              depth={1}
              groupLayers={groupLayers[current.area.inspection_area_id]}
              isAllowedToModify={false}
              onCheckToggled={(ids, selected) => handleCheckToggle(current.area.inspection_area_id, ids, selected)}
              onVisibilityToggled={handleToggleVisibility}
            />

            {/* Unlabeled elements list */}
            <VStack w="100%" gap={0} pb={INFO_PANEL_PADDING - 1}>
              {toolPanelItems[current.area.inspection_area_id].map((item) => (
                <Layer
                  key={item.key}
                  item={item}
                  depth={0}
                  isParentWorking={item.hasWorkingItems}
                  onCheckToggled={(ids, selected) => handleCheckToggle(current.area.inspection_area_id, ids, selected)}
                  onSelect={handleSelectLayer}
                  onVisibilityToggled={handleToggleVisibility}
                />
              ))}
            </VStack>
          </Box>
        </Box>
      ))}
    </Box>
  )
}

export default EditorElements
